import { useEffect, useState } from "react";
import { OrderDetailModal, Orders, OrderStatusModal } from "../components";
import { Tab, Tabs } from "react-bootstrap";
import { Layout } from "../layouts";
import { useAuth, useStore } from "../hooks";
import { GetOrders, MarkOrderFavApi } from "../services";
import { OrderModel } from "../models";
import { Toast } from "../components/Toast";

const count = 10;

const OrdersPage = () => {
  const { storeId } = useStore();
  const { customerId } = useAuth();

  const [activeModal, setActiveModal] = useState<
    "orderDetail" | "orderStatus" | null
  >(null);
  const [selectedOrder, setSelectedOrder] = useState<OrderModel>();

  const handleClose = () => {
    setActiveModal(null);
  };

  const handleRefresh = () => {};

  // Separate states for past and favorite orders
  const [pastOrders, setPastOrders] = useState<OrderModel[]>([]);
  const [favoriteOrders, setFavoriteOrders] = useState<OrderModel[]>([]);

  // Page numbers for each tab

  // Track last loaded pages
  const [lastLoadedPastPage, setLastLoadedPastPage] = useState(1);
  const [lastLoadedFavPage, setLastLoadedFavPage] = useState(1);

  // Active tab & loading states
  const [activeTab, setActiveTab] = useState<"past" | "favourite">("past");
  const [isLoading, setIsLoading] = useState(false);
  const [showLoadMoreBtn, setShowLoadMoreBtn] = useState(true);

  // Get the current tab's state
  const isFavourite = activeTab === "favourite";
  const orders = isFavourite ? favoriteOrders : pastOrders;
  const setOrders = isFavourite ? setFavoriteOrders : setPastOrders;
  const lastLoadedPage = isFavourite ? lastLoadedFavPage : lastLoadedPastPage;
  const setLastLoadedPage = isFavourite
    ? setLastLoadedFavPage
    : setLastLoadedPastPage;

  // Fetch orders when the tab is selected
  useEffect(() => {
    if (orders.length > 0) return; // Don't re-fetch if orders exist
    fetchOrders(1, true);
  }, [storeId, customerId, activeTab]);

  // Fetch orders function
  const fetchOrders = async (currentPage: number, reset: boolean = false) => {
    setIsLoading(true);
    try {
      const { status, data } = await GetOrders({
        store_id: storeId,
        customer_id: customerId,
        is_favourite: isFavourite,
        page: currentPage,
      });

      if (status) {
        setOrders((prevOrders) => (reset ? data : [...prevOrders, ...data]));
        setShowLoadMoreBtn(data.length > 0 && data.length === count);
        setLastLoadedPage(currentPage); // Store last successful fetch
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Load More button click
  const handleLoadMore = () => {
    const nextPage = lastLoadedPage + 1; // Use last successfully fetched page
    fetchOrders(nextPage);
  };

  // Handle Tab Change
  const handleSelectTab = (eventKey: string | null) => {
    if (eventKey !== "past" && eventKey !== "favourite") return;
    setActiveTab(eventKey);
    setShowLoadMoreBtn(true);
  };

  const handleFavOrder = async (order: OrderModel) => {
    try {
      const { status, data } = await MarkOrderFavApi({
        store_id: storeId,
        customer_id: customerId,
        order_id: order.order_id,
        is_fav: !order.is_favourite,
        is_fav_name: "",
      });

      if (status) {
        if (data.is_favourite) {
          // Mark as favorite: remove from past orders, add to favorite orders
          setPastOrders((prevOrders) =>
            prevOrders.filter((item) => item.order_id !== order.order_id),
          );
          setFavoriteOrders((prevOrders) => [data, ...prevOrders]);
        } else {
          // Unmark as favorite: just remove from favorite orders (already in past orders)
          setFavoriteOrders((prevOrders) =>
            prevOrders.filter((item) => item.order_id !== order.order_id),
          );
        }

        Toast.success(
          data.is_favourite
            ? "Order added to favorite!"
            : "Order removed from favorite!",
        );
      }
    } catch (error) {
      console.error("Error updating favourite status:", error);
    } finally {
      handleClose();
    }
  };

  return (
    <Layout showLoader={isLoading}>
      <div className="choose_payment_method_main dashbord_main order_main">
        <div className="order_menu">
          <Tabs
            activeKey={activeTab}
            className="mb-3"
            onSelect={handleSelectTab}
          >
            <Tab eventKey="past" title="Past Orders">
              <Orders
                isLoading={isLoading}
                orders={orders}
                onTrack={(order) => {
                  setSelectedOrder(order);
                  setActiveModal("orderStatus");
                }}
                onShow={(order) => {
                  setSelectedOrder(order);
                  setActiveModal("orderDetail");
                }}
              />
            </Tab>
            <Tab eventKey="favourite" title="Favorite Orders">
              <Orders
                isLoading={isLoading}
                orders={orders}
                onTrack={(order) => {
                  setSelectedOrder(order);
                  setActiveModal("orderStatus");
                }}
                onShow={(order) => {
                  setSelectedOrder(order);
                  setActiveModal("orderDetail");
                }}
              />
            </Tab>
          </Tabs>

          {!isLoading && showLoadMoreBtn && (
            <div className="text-center">
              <button
                className="btn modal-button w-auto"
                onClick={handleLoadMore}
              >
                Load More
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          show={activeModal === "orderDetail"}
          handleClose={handleClose}
          handleFavOrder={() => handleFavOrder(selectedOrder)}
        />
      )}

      {selectedOrder && (
        <OrderStatusModal
          order={selectedOrder}
          show={activeModal === "orderStatus"}
          handleClose={handleClose}
          onRefresh={handleRefresh}
        />
      )}
    </Layout>
  );
};

export default OrdersPage;
